@let drawFarmPoints = drawFarmPoints$ | async;
@let drawMode = drawMode$ | async;
@let drawModeType = this.drawModeType$ | async;
@let mapOptions = mapOptions$ | async;
@let mapCenter = mapCenter$ | async;

@if (mapOptions && mapCenter) {
  @let searchLocation = searchLocation$ | async;
  @let markerOptions = markerOptions$ | async;
  @let drawFarmCenter = drawFarmCenter$ | async;

  <google-map
    height="100%"
    width="100%"
    [center]="mapCenter"
    [options]="mapOptions"
    (mapInitialized)="mapInitialized($event)"
    (mapDragend)="mapDragend()"
    (mapClick)="onMapClick($event)"
  >
    <red-user-position></red-user-position>

    @if (drawMode === false && searchLocation && markerOptions) {
      <map-marker [position]="searchLocation" [options]="markerOptions"> </map-marker>
    }

    @if (drawMode && drawModeType === walkingFarmNewTabs.Radius) {
      @let drawFarmMarkerOptions = drawFarmMarkerOptions$ | async;
      @let drawFarmRadiusInMeters = drawFarmRadiusInMeters$ | async;

      @if (drawFarmCenter && drawFarmMarkerOptions) {
        <map-marker [position]="drawFarmCenter" [options]="drawFarmMarkerOptions"> </map-marker>
      }

      @if (drawFarmRadiusInMeters) {
        @if (drawFarmCenter) {
          <map-circle [center]="drawFarmCenter" [radius]="drawFarmRadiusInMeters" [options]="circleOptions" />
        } @else {
          <map-circle [radius]="drawFarmRadiusInMeters" [options]="circleOptions" />
        }
      }
    }
    @if (drawMode && drawModeType === walkingFarmNewTabs.Polygon) {
      @if (drawFarmPoints !== null && drawFarmPoints.length) {
        <map-polyline [path]="drawFarmPoints" [options]="polyLineOptions"></map-polyline>
      }
      @for (farmPoint of drawFarmPoints; track farmPoint) {
        <map-circle
          [center]="farmPoint"
          [options]="polyLinePointsOptions"
          (circleClick)="onPolyLinePointClick(farmPoint)"
        ></map-circle>
      }
    }
    @if (drawFarmCompleted$ | async) {
      @if (drawFarmPoints !== null && drawFarmPoints.length) {
        <map-polygon [paths]="drawFarmPoints" [options]="polygonOptions"></map-polygon>
      }
    }
  </google-map>
}

<div class="tw:absolute tw:top-0 tw:right-1/2 tw:w-full tw:max-w-2xl tw:translate-x-1/2">
  @if (drawMode === false) {
    <red-map-search-address></red-map-search-address>
    <red-map-default-options>
      <red-user-position-center-button></red-user-position-center-button>
    </red-map-default-options>
  }
  @if (drawMode) {
    <div class="tw:mx-auto tw:mt-4 tw:w-9/12">
      <div class="tw:rounded-lg tw:bg-white">
        <ion-segment
          id="mode-tabs"
          (ionChange)="drawModeTypeChange($event)"
          class="draw-mode-type"
          [value]="drawModeType ?? walkingFarmNewTabs.Polygon"
        >
          <ion-segment-button [value]="walkingFarmNewTabs.Polygon">
            <ion-label>Polygon</ion-label>
          </ion-segment-button>
          <ion-segment-button [value]="walkingFarmNewTabs.Radius">
            <ion-label>Radius</ion-label>
          </ion-segment-button>
        </ion-segment>
      </div>
    </div>
  }
</div>
@if (drawMode) {
  @switch (drawModeType) {
    @case (walkingFarmNewTabs.Radius) {
      <div class="tw:absolute tw:right-1/2 tw:bottom-0 tw:w-full tw:max-w-2xl tw:translate-x-1/2">
        <ion-card class="tw:mx-2 tw:my-1">
          <ion-card-header class="tw:px-4 tw:pt-1 tw:pb-0">
            <ion-card-subtitle>Radius: {{ drawFarmRadius$ | async }} mi.</ion-card-subtitle>
          </ion-card-header>
          <ion-card-content class="tw:px-4 tw:pb-1">
            <ion-range
              [value]="(drawFarmRadius$ | async) ?? minRadiusRangeValue"
              (ionChange)="drawFarmRadiusChange($event)"
              class="tw:pt-0"
              [ticks]="true"
              [snaps]="true"
              [pin]="false"
              [min]="minRadiusRangeValue"
              [step]="0.1"
              [max]="2"
              aria-label="Radius"
            ></ion-range>
          </ion-card-content>
        </ion-card>
      </div>
      <ion-fab class="tw:bottom-20" slot="fixed" horizontal="center" vertical="bottom">
        <div class="tw:flex tw:text-center">
          <ion-fab-button
            size="small"
            color="light"
            (click)="clearDrawFarm()"
            class="tw:mx-2"
            [disabled]="(drawFarmCenter$ | async) === null"
          >
            <ion-icon src="/assets/svg/eraser.svg"></ion-icon>
          </ion-fab-button>
          <ion-fab-button size="small" color="danger" (click)="toggleDrawMode()" class="tw:mx-2">
            <ion-icon name="close-circle-outline"></ion-icon>
          </ion-fab-button>
          <ion-fab-button
            [disabled]="(drawFarmCenter$ | async) === null"
            (click)="loadFarmSummary()"
            color="primary"
            class="tw:mx-2"
          >
            <ion-icon src="/assets/svg/wand.svg"></ion-icon>
          </ion-fab-button>
        </div>
      </ion-fab>
    }
    @default {
      <ion-fab slot="fixed" horizontal="center" vertical="bottom">
        <div class="tw:flex tw:text-center">
          <ion-fab-button
            color="light"
            (click)="undoDrawFarmPoint()"
            class="tw:mx-2"
            [disabled]="!drawFarmPoints?.length"
          >
            <ion-icon src="/assets/svg/undo.svg"></ion-icon>
          </ion-fab-button>
          <ion-fab-button
            size="small"
            color="light"
            (click)="clearDrawFarm()"
            class="tw:mx-2"
            [disabled]="!drawFarmPoints?.length"
          >
            <ion-icon src="/assets/svg/eraser.svg"></ion-icon>
          </ion-fab-button>
          <ion-fab-button size="small" color="danger" (click)="toggleDrawMode()" class="tw:mx-2">
            <ion-icon name="close-circle-outline"></ion-icon>
          </ion-fab-button>
          @if ((drawFarmCompleted$ | async) === false) {
            <ion-fab-button
              color="primary"
              (click)="autoFillDrawFarm()"
              class="tw:mx-2"
              [disabled]="drawFarmPoints === null || drawFarmPoints.length < 3"
            >
              <ion-icon src="/assets/svg/check.svg"></ion-icon>
            </ion-fab-button>
          }
          @if (drawFarmCompleted$ | async) {
            <ion-fab-button (click)="loadFarmSummary()" color="primary" class="tw:mx-2">
              <ion-icon src="/assets/svg/wand.svg"></ion-icon>
            </ion-fab-button>
          }
        </div>
      </ion-fab>
    }
  }
} @else {
  <ion-fab slot="fixed" horizontal="center" vertical="bottom">
    <ion-fab-button (click)="toggleDrawMode()">
      <ion-icon src="/assets/svg/pencil.svg"></ion-icon>
    </ion-fab-button>
  </ion-fab>
}
